import { ApplicationForm, isPrepay } from './types';
import { effectiveLocationAddress } from './locations';

// Section 1-2 scalars sent to POST /applications/start (materializes the DRAFT).
// Keys match the server's pickDraftScalars.
export function buildStartPayload(form: ApplicationForm) {
  return {
    legalName: form.legalName,
    dba: form.dba,
    businessType: form.businessType,
    entityType: form.entityType,
    yearIncorporated: form.yearIncorporated,
    stateOfIncorporation: form.stateOfIncorporation,
    ein: form.ein,
    officeLine1: form.officeLine1,
    officeLine2: form.officeLine2,
    officeCity: form.officeCity,
    officeState: form.officeState,
    officeZip: form.officeZip,
    officePhone: form.officePhone,
    officeFax: form.officeFax,
    officeWebsite: form.officeWebsite,
    ownerFirstName: form.ownerFirstName,
    ownerLastName: form.ownerLastName,
    ownerTitle: form.ownerTitle,
    ownerMobile: form.ownerMobile,
    ownerEmail: form.ownerEmail,
    heardAboutUs: form.heardAboutUs,
    heardAboutUsDetail: form.heardAboutUsDetail,
    billingContact: form.billingContact,
    billingPhone: form.billingPhone,
    billingEmail: form.billingEmail,
    invoiceMethod: form.invoiceMethod,
  };
}

// Full payload for POST /applications/submit. The prepay branch drops bank /
// references / bankruptcy / SSNs / guarantee so nothing sensitive is sent.
export function buildSubmitPayload(form: ApplicationForm, recaptchaToken: string) {
  const prepay = isPrepay(form);

  return {
    // Business / owner / billing scalars too, so a resubmit persists edits to them.
    ...buildStartPayload(form),
    recaptchaToken,
    creditLine: form.creditLine,
    annualTonsRange: form.annualTonsRange,

    // The office becomes a (primary/HQ) delivery address when flagged at the start.
    officeIsPrimary: form.officeIsPrimary,
    officeIsHeadquarters: form.officeIsHeadquarters,

    // A "same as previous" row resolves its address live (not a stale copy).
    locations: form.locations.map((l, i) => {
      const addr = effectiveLocationAddress(form, i);
      return {
        name: l.name,
        line1: addr.line1,
        line2: addr.line2,
        city: addr.city,
        state: addr.state,
        zip: addr.zip,
        deliveryNotes: l.deliveryNotes,
        isPrimary: l.isPrimary,
        isHeadquarters: l.isHeadquarters,
      };
    }),

    principals: form.principals.map((p) => ({
      fullName: p.fullName,
      title: p.title,
      percentOwned: p.percentOwned,
      homeLine1: p.homeLine1,
      homeLine2: p.homeLine2,
      homeCity: p.homeCity,
      homeState: p.homeState,
      homeZip: p.homeZip,
      // SSN only leaves the browser on the credit path.
      ssn: prepay ? '' : p.ssn,
    })),

    tradeReferences: prepay
      ? []
      : form.references.map((r) => ({
          companyName: r.companyName,
          contactName: r.contactName,
          phone: r.phone,
          email: r.email,
          line1: r.line1,
          city: r.city,
          state: r.state,
          zip: r.zip,
        })),

    bankName: prepay ? '' : form.bankName,
    bankPhone: prepay ? '' : form.bankPhone,
    bankLine1: prepay ? '' : form.bankLine1,
    bankCity: prepay ? '' : form.bankCity,
    bankState: prepay ? '' : form.bankState,
    bankZip: prepay ? '' : form.bankZip,
    bankContact: prepay ? '' : form.bankContact,
    bankAccountType: prepay ? '' : form.bankAccountType,

    hasBankruptcy: prepay ? false : form.hasBankruptcy === 'yes',
    bankruptcyExplanation: prepay ? '' : form.bankruptcyExplanation,

    teamMembers: form.teamMembers.map((m) => ({
      name: m.name,
      email: m.email,
      role: m.role,
      locationAccess: m.locationAccess,
    })),

    productInterestIds: form.productInterestIds,

    taxExempt: form.taxExempt === 'yes',
    resaleCertState: form.resaleCertState,
    resaleSellerType: form.resaleSellerType,
    resaleInBusinessOf: form.resaleInBusinessOf,
    resaleRegistrationNumber: form.resaleRegistrationNumber,
    resaleDescription: form.resaleDescription,

    termsVersion: '2026-defeo-v1',
    agreedToTerms: form.agreedToTerms,
    agreedPersonalGuarantee: prepay ? false : form.agreedPersonalGuarantee,
    signerTitle: form.signerTitle,
    signerTypedSignature: form.signerTypedSignature,
  };
}
